<template>
    <div
        v-if="show"
        class="modal fade show"
        style="display: block; background-color: rgba(0,0,0,0.5);"
        tabindex="-1"
        role="dialog"
        aria-labelledby="vehicleDetailsModalTitle"
        aria-hidden="true"
        @click.self="closeModal"
    >
        <div class="modal-dialog modal-dialog-centered modal-lg" role="document">
            <div class="modal-content">
                <!-- Cabeçalho do Modal -->
                <div class="modal-header">
                    <h5 class="modal-title" id="vehicleDetailsModalTitle">
                        <i class="fas fa-car mr-2"></i>
                        Informações Detalhadas do Veículo
                    </h5>
                    <button
                        type="button"
                        class="close"
                        @click="closeModal"
                        aria-label="Fechar"
                    >
                        <span aria-hidden="true">&times;</span>
                    </button>
                </div>

                <!-- Corpo do Modal -->
                <div class="modal-body" v-if="vehicle">
                    <div class="row">
                        <!-- Imagem do Veículo -->
                        <div class="col-md-5">
                            <div class="vehicle-image-container">
                                <img
                                    :src="vehicle.photo || defaultImage"
                                    :alt="`Imagem do ${vehicle.model || 'Veículo'}`"
                                    class="vehicle-image"
                                    @error="handleImageError"
                                />

                                <!-- Badge de Status -->
                                <div class="status-badge" v-if="vehicle.status">
                                    <i class="fas fa-circle status-indicator"></i>
                                    {{ vehicle.status }}
                                </div>
                            </div>
                        </div>

                        <!-- Informações Detalhadas -->
                        <div class="col-md-7">
                            <div class="vehicle-details">
                                <!-- Título/Modelo -->
                                <div class="detail-section">
                                    <h6 class="section-title">
                                        <i class="fas fa-tag mr-2"></i>
                                        Identificação
                                    </h6>
                                    <div class="details-grid">
                                        <DetailItem
                                            label="Modelo"
                                            :value="vehicle.model || vehicle.description"
                                            icon="fas fa-car"
                                        />
                                        <DetailItem
                                            v-if="shouldShowPlate"
                                            label="Placa"
                                            :value="vehicle.board"
                                            icon="fas fa-id-card-alt"
                                        />
                                    </div>
                                </div>

                                <!-- Especificações -->
                                <div class="detail-section">
                                    <h6 class="section-title">
                                        <i class="fas fa-cogs mr-2"></i>
                                        Especificações
                                    </h6>
                                    <div class="details-grid">
                                        <DetailItem
                                            label="Ano/Modelo"
                                            :value="vehicle.model_year"
                                            icon="fas fa-calendar-alt"
                                        />
                                        <DetailItem
                                            label="Marca"
                                            :value="vehicle.brand"
                                            icon="fas fa-industry"
                                        />
                                        <DetailItem
                                            label="Cor"
                                            :value="vehicle.color"
                                            icon="fas fa-palette"
                                        />
                                        <DetailItem
                                            label="Combustível"
                                            :value="vehicle.combustible"
                                            icon="fas fa-gas-pump"
                                        />
                                        <DetailItem
                                            label="Lugares"
                                            :value="vehicle.places"
                                            icon="fas fa-users"
                                        />
                                    </div>
                                </div>

                                <!-- Informações Operacionais -->
                                <div class="detail-section">
                                    <h6 class="section-title">
                                        <i class="fas fa-chart-line mr-2"></i>
                                        Informações Operacionais
                                    </h6>
                                    <div class="details-grid">
                                        <DetailItem
                                            label="Km do Veículo"
                                            :value="formatKilometers(vehicle.current_km)"
                                            icon="fas fa-tachometer-alt"
                                            highlight
                                        />
                                        <DetailItem
                                            v-if="vehicle.cost_center"
                                            label="Centro de Custo"
                                            :value="vehicle.cost_center.description"
                                            icon="fas fa-building"
                                        />
                                    </div>
                                </div>

                                <!-- Informações Adicionais -->
                                <div class="detail-section" v-if="hasAdditionalInfo">
                                    <h6 class="section-title">
                                        <i class="fas fa-info-circle mr-2"></i>
                                        Informações Adicionais
                                    </h6>
                                    <div class="details-grid">
                                        <DetailItem
                                            v-if="vehicle.observations"
                                            label="Observações"
                                            :value="vehicle.observations"
                                            icon="fas fa-comment"
                                        />
                                        <DetailItem
                                            v-if="vehicle.maintenance_date"
                                            label="Última Manutenção"
                                            :value="formatDate(vehicle.maintenance_date)"
                                            icon="fas fa-wrench"
                                        />
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <!-- Rodapé do Modal -->
                <div class="modal-footer">
                    <button
                        type="button"
                        class="btn btn-secondary"
                        @click="closeModal"
                    >
                        <i class="fas fa-times mr-2"></i>
                        Fechar
                    </button>

                    <button
                        v-if="!isConsultationOnly && vehicle"
                        type="button"
                        class="btn btn-primary"
                        @click="selectVehicle"
                    >
                        <i class="fas fa-check mr-2"></i>
                        Selecionar Veículo
                    </button>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import DetailItem from './DetailItem.vue';

export default {
    name: 'VehicleDetailsModal',
    components: {
        DetailItem,
    },
    props: {
        vehicle: {
            type: Object,
            default: null
        },
        show: {
            type: Boolean,
            default: false
        },
        isConsultationOnly: {
            type: Boolean,
            default: false
        }
    },
    data() {
        return {
            defaultImage: '/images/default-car.png' // Imagem padrão
        };
    },
    computed: {
        // Verifica se deve exibir a placa (mesma regra do cartão)
        shouldShowPlate() {
            if (!this.vehicle) return false;

            const isRentalVehicle = this.vehicle.is_rental ||
                (this.vehicle.type && this.vehicle.type.toLowerCase().includes('locação')) ||
                (this.vehicle.description && this.vehicle.description.toLowerCase().includes('locação'));

            return !isRentalVehicle;
        },

        // Verifica se há informações adicionais para exibir
        hasAdditionalInfo() {
            return this.vehicle && (
                this.vehicle.observations ||
                this.vehicle.maintenance_date ||
                this.vehicle.additional_info
            );
        }
    },
    methods: {
        // Fecha o modal
        closeModal() {
            this.$emit('close');
        },

        // Seleciona o veículo (emite evento para o componente pai)
        selectVehicle() {
            this.$emit('select-vehicle', this.vehicle);
            this.closeModal();
        },

        // Formata quilometragem
        formatKilometers(km) {
            if (!km) return 'N/A';

            // Remove qualquer formatação existente e converte para número
            const numKm = parseInt(km.toString().replace(/\D/g, ''));

            if (isNaN(numKm)) return 'N/A';

            // Formata com separadores de milhares
            return numKm.toLocaleString('pt-BR') + ' km';
        },

        // Formata data
        formatDate(date) {
            if (!date) return 'N/A';

            try {
                const dateObj = new Date(date);
                return dateObj.toLocaleDateString('pt-BR');
            } catch (error) {
                return 'N/A';
            }
        },

        // Manipula erro de carregamento da imagem
        handleImageError(event) {
            event.target.src = this.defaultImage;
        }
    },
    watch: {
        // Monitora a propriedade show para gerenciar o scroll do body
        show(newValue) {
            if (newValue) {
                document.body.style.overflow = 'hidden';
            } else {
                document.body.style.overflow = '';
            }
        }
    },
    beforeDestroy() {
        // Restaura o scroll ao destruir o componente
        document.body.style.overflow = '';
    }
};
</script>

<style scoped>
.modal {
    z-index: 1050;
}

.modal-content {
    border-radius: 12px;
    border: none;
    box-shadow: 0 10px 40px rgba(0, 0, 0, 0.2);
}

.modal-header {
    background: linear-gradient(135deg, #FE8D18 0%, #0056b3 100%);
    color: white;
    border-radius: 12px 12px 0 0;
    border-bottom: none;
    padding: 20px 25px;
}

.modal-title {
    font-weight: 600;
    font-size: 1.2rem;
}

.close {
    color: white;
    opacity: 0.8;
    font-size: 1.5rem;
    font-weight: 300;
}

.close:hover {
    opacity: 1;
    color: white;
}

.modal-body {
    padding: 25px;
    background-color: #f8f9fa;
}

.vehicle-image-container {
    position: relative;
    background: linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%);
    border-radius: 12px;
    padding: 20px;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
    text-align: center;
}

.vehicle-image {
    max-width: 100%;
    max-height: 300px;
    object-fit: contain;
    border-radius: 8px;
}

.status-badge {
    position: absolute;
    top: 15px;
    right: 15px;
    background: linear-gradient(135deg, #28a745 0%, #20c997 100%);
    color: white;
    padding: 5px 12px;
    border-radius: 20px;
    font-size: 12px;
    font-weight: 600;
    box-shadow: 0 2px 8px rgba(40, 167, 69, 0.3);
}

.status-indicator {
    color: #ffffff;
    margin-right: 5px;
    animation: pulse 2s infinite;
}

.vehicle-details {
    background: white;
    border-radius: 12px;
    padding: 20px;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
    height: 100%;
}

.detail-section {
    margin-bottom: 25px;
}

.detail-section:last-child {
    margin-bottom: 0;
}

.section-title {
    color: #495057;
    font-weight: 600;
    margin-bottom: 15px;
    padding-bottom: 8px;
    border-bottom: 2px solid #e9ecef;
    font-size: 1rem;
}

.details-grid {
    display: grid;
    grid-template-columns: 1fr;
    gap: 10px;
}

.modal-footer {
    background-color: #f8f9fa;
    border-top: 1px solid #e9ecef;
    border-radius: 0 0 12px 12px;
    padding: 20px 25px;
}

.btn {
    border-radius: 8px;
    font-weight: 500;
    padding: 10px 20px;
    transition: all 0.3s ease;
}

.btn-secondary {
    background: linear-gradient(135deg, #6c757d 0%, #545b62 100%);
    border: none;
}

.btn-primary {
    background: linear-gradient(135deg, #FE8D18 0%, #0056b3 100%);
    border: none;
}

.btn:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
}

/* Animações */
@keyframes pulse {
    0% {
        opacity: 1;
    }
    50% {
        opacity: 0.5;
    }
    100% {
        opacity: 1;
    }
}

/* Responsividade */
@media (max-width: 768px) {
    .modal-dialog {
        margin: 10px;
        max-width: calc(100% - 20px);
    }

    .modal-body {
        padding: 20px 15px;
    }

    .vehicle-image-container {
        margin-bottom: 20px;
        padding: 15px;
    }

    .vehicle-image {
        max-height: 200px;
    }

    .vehicle-details {
        padding: 15px;
    }

    .modal-footer {
        padding: 15px;
        text-align: center;
    }

    .modal-footer .btn {
        width: 100%;
        margin-bottom: 10px;
    }

    .modal-footer .btn:last-child {
        margin-bottom: 0;
    }
}

@media (max-width: 576px) {
    .modal-header {
        padding: 15px 20px;
    }

    .modal-title {
        font-size: 1.1rem;
    }

    .detail-section {
        margin-bottom: 20px;
    }

    .section-title {
        font-size: 0.95rem;
    }
}
</style>



// WEBPACK FOOTER //
// src/components/ReserveVehicles/add-components/VehicleDetailsModal.vue